import type React from 'react';
import { cx } from '@/styled-system/css';
import { keyValueContainer, keyValueLabel, keyValueValue } from './keyValue.styles';

export type KeyValueProps = {
  /** The label/key text displayed on the left */
  label: string;
  /** The value content displayed on the right - can be any React node */
  value: React.ReactNode;
  /** Optional CSS class to apply to the container */
  className?: string;
  /** Optional CSS class to apply to the label element */
  labelClassName?: string;
  /** Optional CSS class to apply to the value element */
  valueClassName?: string;
};

/**
 * KeyValue Component
 *
 * A reusable UI primitive for displaying key-value pairs in a consistent
 * two-column layout. The key appears on the left with grey text, and the
 * value appears on the right with bold blue text.
 *
 * **Usage:**
 * ```tsx
 * <KeyValue label="Deposit" value="$200" />
 * <KeyValue label="Status">
 *   <Tag tone="teal">Active</Tag>
 * </KeyValue>
 * ```
 *
 * **Design Specifications:**
 * - Label: text.subtle color, 16px, 400 font weight (regular)
 * - Value: text.neutral color, 16px, 700 font weight (bold)
 * - Layout: Flexbox with space-between alignment via <dl><dt><dd>
 * - Gap: 16px minimum between label and value
 *
 * **Accessibility Features:**
 * - Semantic HTML structure with proper text elements
 * - Keyboard accessible (inherits from containing elements)
 * - Screen reader friendly with clear label-value relationship
 *
 * **Performance:**
 * - Uses Panda CSS for zero-runtime styling
 * - Minimal DOM structure for optimal rendering
 * - Flexbox layout for efficient text alignment
 */
export const KeyValue: React.FC<KeyValueProps> = ({ label, value, className, labelClassName, valueClassName }) => {
  // Convert primitive values to strings for proper rendering
  const renderValue = () => {
    if (typeof value === 'boolean' || typeof value === 'number') {
      return String(value);
    }
    return value;
  };

  return (
    <dl className={cx(keyValueContainer, className)}>
      <dt className={cx(keyValueLabel, labelClassName)}>{label}</dt>
      <dd className={cx(keyValueValue, valueClassName)}>{renderValue()}</dd>
    </dl>
  );
};
